import { feedItemKey } from './feedNewItems';

const IMPRESSION_DELAY_MS = 1000;
const MIN_DWELL_MS = 3000;
const MAX_DWELL_MS = 300000;

export function meaningfulVisibleHeight(cardHeight, viewportHeight) {
    return Math.min(Math.max(cardHeight, 0) * 0.5, Math.max(viewportHeight, 0) * 0.5);
}

export function isMeaningfullyVisible({ visibleHeight, cardHeight, viewportHeight }) {
    const threshold = meaningfulVisibleHeight(cardHeight, viewportHeight);
    return threshold > 0 && visibleHeight > 0 && visibleHeight >= threshold;
}

export function clampVisibleMs(value) {
    return Math.max(0, Math.min(Number(value) || 0, MAX_DWELL_MS));
}

export function createFeedVisitId() {
    if (typeof window !== 'undefined' && window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `feed-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export class FeedTelemetry {
    constructor({ appState, surface = 'feed', root = null }) {
        this.appState = appState;
        this.surface = surface;
        this.root = root;
        this.visitId = createFeedVisitId();
        this.cards = new Map();
        this.elementKeys = new Map();
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
        }
        if (typeof IntersectionObserver !== 'undefined') {
            this.observer = new IntersectionObserver(entries => this.handleEntries(entries), {
                root,
                threshold: Array.from({ length: 101 }, (value, index) => index / 100),
            });
        }
    }

    emit(type, props = {}) {
        this.appState?.addUsageLog({
            type,
            props: {
                visitId: this.visitId,
                surface: this.surface,
                ...props,
            },
            timestamp: Math.floor(Date.now() / 1000),
        });
    }

    startVisit({ source = 'mount', offline = false } = {}) {
        this.cards.forEach(card => this.exitCard(card));
        this.visitId = createFeedVisitId();
        this.cards.forEach(card => {
            card.impressed = false;
            card.dwellEmitted = false;
            card.visible = false;
            card.visibleStartedAt = null;
            card.accumulatedMs = 0;
            if (card.intersectionVisible && document.visibilityState === 'visible') {
                this.enterCard(card);
            }
        });
        if (['feed', 'personalized'].includes(this.surface)) {
            this.emit('feed_visit', { source, offline: Boolean(offline) });
        }
        return this.visitId;
    }

    observeElement = (item, element) => {
        const key = feedItemKey(item);
        if (!key) return;
        const existing = this.cards.get(key);
        if (!element) {
            if (existing?.element && this.observer) this.observer.unobserve(existing.element);
            this.finishCard(key);
            return;
        }
        const card = existing || {
            key,
            item,
            element,
            impressed: false,
            dwellEmitted: false,
            visible: false,
            intersectionVisible: false,
            visibleStartedAt: null,
            accumulatedMs: 0,
            timer: null,
        };
        card.item = item;
        card.element = element;
        this.cards.set(key, card);
        this.elementKeys.set(element, key);
        element.dataset.feedKey = key;
        this.observer?.observe(element);
    }

    handleEntries(entries) {
        entries.forEach(entry => {
            const key = this.elementKeys.get(entry.target);
            const card = this.cards.get(key);
            if (!card) return;
            const viewportHeight = this.root?.clientHeight || window.innerHeight || document.documentElement.clientHeight;
            const visible = isMeaningfullyVisible({
                visibleHeight: entry.intersectionRect?.height || 0,
                cardHeight: entry.boundingClientRect?.height || entry.target.offsetHeight || 0,
                viewportHeight,
            });
            card.intersectionVisible = visible;
            if (visible && document.visibilityState === 'visible') {
                this.enterCard(card);
            } else if (document.visibilityState !== 'visible') {
                this.pauseCard(card);
            } else {
                this.exitCard(card);
            }
        });
    }

    enterCard(card) {
        if (card.visible) return;
        card.visible = true;
        card.visibleStartedAt = Date.now();
        if (!card.impressed && !card.timer) {
            card.timer = setTimeout(() => {
                card.timer = null;
                if (!card.visible || document.visibilityState !== 'visible') return;
                card.impressed = true;
                card.visibleStartedAt = Date.now();
                this.emit('feed_impression', {
                    contentType: card.item._feedType === 'news' ? 'news' : 'article',
                    contentId: Number(card.item.id),
                });
            }, IMPRESSION_DELAY_MS);
        }
    }

    pauseCard(card) {
        if (card.timer) {
            clearTimeout(card.timer);
            card.timer = null;
        }
        if (!card.visible) return;
        if (card.impressed && card.visibleStartedAt) {
            card.accumulatedMs += Date.now() - card.visibleStartedAt;
        }
        card.visible = false;
        card.visibleStartedAt = null;
    }

    exitCard(card) {
        this.pauseCard(card);
        if (card.impressed && !card.dwellEmitted && card.accumulatedMs >= MIN_DWELL_MS) {
            card.dwellEmitted = true;
            this.emit('feed_dwell', {
                contentType: card.item._feedType === 'news' ? 'news' : 'article',
                contentId: Number(card.item.id),
                visibleMs: clampVisibleMs(card.accumulatedMs),
            });
        }
    }

    finishCard(key) {
        const card = this.cards.get(key);
        if (!card) return;
        this.exitCard(card);
        if (card.element) this.elementKeys.delete(card.element);
        this.cards.delete(key);
    }

    handleVisibilityChange() {
        this.cards.forEach(card => {
            if (document.visibilityState !== 'visible') {
                this.pauseCard(card);
            } else if (card.intersectionVisible) {
                this.enterCard(card);
            }
        });
    }

    disconnect() {
        this.cards.forEach((card, key) => this.finishCard(key));
        this.observer?.disconnect();
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        }
    }
}

export { IMPRESSION_DELAY_MS, MIN_DWELL_MS, MAX_DWELL_MS };
